// React Hooks
import React, { useEffect, useRef, useState, useContext, useCallback } from "react";
import PortalOverlay from "../portal-overlay/portalOverlay";
import { useNavigate, Link } from "react-router-dom";

// Styles
import Styles from "./topBarNew.module.scss";
import PopupStyles from "../popup.module.scss";

// Components
import ChangePassword from "../../components/application/my-account/change-password/changePassword";
import Sidebar from "../sidebar/sidebar";
import Toast from "../toast/toast";
import Button from "../button/button";

// Constants
import DocsSVGnew from "../svg/DocsSVGnew";
import QuestionMarkSVG from "../svg/questionMarkSVG";
import { SECONDARYCOLOR, ACCENTCOLOR } from "../colors";
import HamburgerSVG from "../svg/hamburgerSVG";
import { pageNameMap } from "../../constants/data";
import ArrowUp from "../svg/arrowUp";
import UserDropdown from "./UserDropdown";
import { logout } from "../../utils/logout";
import { primaryButtonStyle, primaryButtonHoverStyle } from "../buttonStyles";
import { MEDIUM } from "../buttonSize";
import Switch from "../toggle-switch/switch";
import { environmentContext } from "../../context/environmentContext";
import { Info } from "lucide-react";
import { ONBOARDING_STATUS, normalizeOnboardingStatus } from "../../constants/onboardingStatuses";
import Tooltip from "../tooltip/tooltip";
import { testIdProps } from "../../utils/testIdProps";

function TopBar({ userDetails, primarySandboxWallet, onboardingStatus }: any) {
  const history = useNavigate();

  // Parse route from pathname only (without query params)
  const route: any = window.location.pathname.split("/");
  const currentPage = route.at(-1);

  const [pageName, setPageName] = useState(pageNameMap[currentPage]);
  const userObj: any = localStorage.getItem("@user");
  // Ensure user is parsed only if userObj exists, otherwise null
  const user = userObj ? JSON.parse(userObj) : null;

  // Environment context
  const environmentCtx = useContext(environmentContext);
  const { environment, setEnvironment } = environmentCtx;

  // Check if we're on billing page or trial-center page
  const isBillingPage = currentPage === "billing";
  const isTrialCenterPage = currentPage === "trial-center";
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [toggleSideBar, setToggleSidebar] = useState(false);
  const [toggleCollapsedView, setToggleCollapsedView] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Dropdown state and ref for outside click
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null); // Renamed from profileDropdownRef for consistency
  const logoutCalledRef = useRef(false);

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  function handleChangePassword(data: string) {
    setShowChangePassword(false);
    toastTimeout(true, "success", data);
  }

  function handlePasswordChangeSuccess() {
    // Show reauth modal after change password modal is closed
    setShowReauthModal(true);
    setCountdown(5);
    logoutCalledRef.current = false;
  }

  const handleLogoutAndNavigate = useCallback(async () => {
    if (logoutCalledRef.current) return; // Prevent multiple calls
    logoutCalledRef.current = true;
    setShowReauthModal(false);
    setCountdown(0);
    // Call logout API - this will redirect to login page
    await logout({ redirectToLogin: true });
  }, []);

  function handleReauthModalConfirm() {
    handleLogoutAndNavigate();
  }

  async function handleLogout() {
    await logout({ redirectToLogin: true });
  }

  useEffect(() => {
    setPageName(pageNameMap[currentPage]);
  }, [currentPage]);

  // Prevent users from accessing production environment via URL manipulation
  useEffect(() => {
    if (
      isBillingPage &&
      environment === "production" &&
      onboardingStatus &&
      normalizeOnboardingStatus(onboardingStatus) !==
        ONBOARDING_STATUS.PLAN_SUBSCRIBED
    ) {
      setEnvironment("sandbox");
    }
  }, [environment, onboardingStatus, isBillingPage, setEnvironment]);

  // Reset countdown when modal is shown
  useEffect(() => {
    if (showReauthModal && !logoutCalledRef.current) {
      setCountdown(5);
      logoutCalledRef.current = false; // Reset the ref when modal is shown
    }
  }, [showReauthModal]);

  // Countdown timer effect
  useEffect(() => {
    if (!showReauthModal || countdown <= 0 || logoutCalledRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prevCountdown) => {
        const newCountdown = prevCountdown - 1;
        if (newCountdown === 0 && !logoutCalledRef.current) {
          // Call logout when countdown reaches 0
          handleLogoutAndNavigate();
          return 0; // Keep countdown at 0
        }
        return newCountdown;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [showReauthModal, countdown, handleLogoutAndNavigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleBackClick = () => {
    route.includes("manage-apps") && history("/application/manage-apps");
  };

  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEnvironment = e.target.checked ? "production" : "sandbox";
    // If user is trying to switch to production but is not plan subscribed, keep it in sandbox
    if (
      newEnvironment === "production" &&
      normalizeOnboardingStatus(onboardingStatus) !==
        ONBOARDING_STATUS.PLAN_SUBSCRIBED
    ) {
      setEnvironment("sandbox");
      return;
    }

    setEnvironment(newEnvironment);
  };

  // Helper function to format wallet balance
  const formatWalletBalance = (balance: number) => {
    if (!balance) return "₹ 0";
    return `₹ ${balance.toLocaleString()}`;
  };

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      {/* Sandbox Mode Banner - Thin line overlay */}
      {environment === "sandbox" && isBillingPage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            backgroundColor: "#FCD34D",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#92400E",
              backgroundColor: "#FCD34D",
              padding: "2px 16px",
              textTransform: "uppercase",
              letterSpacing: "0.025em",
              whiteSpace: "nowrap",
              position: "relative",
              top: 10,
              display: "flex",
              gap: 10,
              clipPath:
                "polygon(0% 2px, 2px 0%, calc(100% - 2px) 0%, 100% 2px, calc(100% - 6px) calc(100% - 2px), calc(100% - 8px) 100%, 8px 100%, 6px calc(100% - 2px))",
            }}
          >
            <Info size={16} /> You are in sandbox mode
          </span>
        </div>
      )}

      {/* Main TopBar */}
      <div
        style={{
          // backgroundColor: "red",
          // borderBottom: "1px solid #dee2e6",
          padding: "0.75rem 1.5rem",
        }}
        className="d-flex justify-content-between align-items-center px-8"
      >
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            {...testIdProps("top-bar-hamburger-menu")}
            aria-label={toggleSideBar ? "Close menu" : "Open menu"}
            className={`${Styles.hamburgerButton} ${Styles.showOnMobileDevices}`}
            onClick={() => setToggleSidebar(!toggleSideBar)}
          >
            <HamburgerSVG color="#232323" size={20} />
          </button>
          {pageName ? (
            <p
              className="navbar-brand mb-0"
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#212529",
                marginRight: "auto",
              }}
            >
              {pageName}
            </p>
          ) : (
            <>
              {route.at(-2) === "manage-apps" ? (
                <div
                  className="d-flex align-items-center"
                  {...testIdProps("top-bar-new-arrow-up")}
                  onClick={handleBackClick}
                  style={{ cursor: "pointer" }}
                >
                  <div className={`px-1 ${Styles.rotate}`}>
                    <ArrowUp color={SECONDARYCOLOR} />
                  </div>
                  <p className={`mb-0 ${Styles.backText}`}>back</p>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div
          className={`${Styles.topNavBackground} d-flex align-items-center sticky-top`}
          style={{
            zIndex: 2,
          }}
        >

          {isTrialCenterPage && primarySandboxWallet && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "4px",
                marginRight: "14px",
                backgroundColor: "#F3F4F6",
                padding: "4px 12px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555555",
                borderRight: "1px solid #DDDDDD",
              }}
            >
              <span>Primary Wallet (Test)</span>
              <span style={{ color: "#000", fontWeight: 600 }}>
                {formatWalletBalance(
                  primarySandboxWallet?.wallets_balance_with_gst || 0
                )}
              </span>
            </div>
          )}

          {isBillingPage && (
            <div
              className="d-flex align-items-center gap-2"
              style={{ marginRight: "15px" }}
            >
              <span className="text-sm fw-medium">
                {environment === "production" ? "Production" : "Sandbox"}
              </span>

              {normalizeOnboardingStatus(onboardingStatus) !==
              ONBOARDING_STATUS.PLAN_SUBSCRIBED ? (
                <Tooltip
                  placement="bottom"
                  title="Subscribe new plan to activate the production mode"
                >
                  <div>
                    <Switch
                      positiveLabel=""
                      negativeLabel=""
                      checked={environment === "production"}
                      onChange={handleEnvironmentChange}
                      id="environment-switch"
                    />
                  </div>
                </Tooltip>
              ) : (
                <Switch
                  positiveLabel=""
                  negativeLabel=""
                  checked={environment === "production"}
                  onChange={handleEnvironmentChange}
                  id="environment-switch"
                />
              )}
            </div>
          )}

          <div className="ml-auto">
            <div className="d-flex align-items-center flex-row justify-content-center">
              <Link
                to="/doc"
                {...testIdProps("topbar-docs-link")}
                className="d-flex text-decoration-none align-items-center gap-2"
              >
                <DocsSVGnew />
                <p
                  className="mb-0 "
                  style={{ color: "#515151" }}
                  // className={`text-sm text-decoration-none text-gray fw-medium`}
                >
                  Documentation
                </p>
              </Link>
              {/* TODO: will be use later when notification func will be added  */}
              {/* <div className="px-2 mb-1">
                <div>
                  <AlertSVG classes={Styles.SVGNotification} />
                </div>
              </div> */}
              <div className="px-2 ">
                <a
                  href="https://zoopone.freshdesk.com/support/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  {...testIdProps("topbar-support-link")}
                  className="d-flex align-items-center"
                >
                  <QuestionMarkSVG classes={Styles.SVGColor} />
                </a>
              </div>
              {/* Profile Dropdown */}
              <UserDropdown
                user={user}
                userDetails={userDetails}
                handleLogout={handleLogout}
                setShowChangePassword={setShowChangePassword}
              />
            </div>
          </div>
          {showChangePassword ? (
            <ChangePassword
              onSubmit={(data: string) => handleChangePassword(data)}
              onCancel={() => setShowChangePassword(false)}
              onPasswordChangeSuccess={handlePasswordChangeSuccess}
            />
          ) : null}
          {showReauthModal ? (
            <PortalOverlay>
            <div
              className={`d-flex align-items-center justify-content-center ${PopupStyles.overlay}`}
              style={{ zIndex: 10000 }}
            >
              <div
                style={{ zIndex: 10001, maxWidth: "400px", width: "90%" }}
                className={`p-4 ${PopupStyles.popUpContent}`}
              >
                <div className="text-center py-3">
                  <h4
                    className="mb-3"
                    style={{ color: ACCENTCOLOR, fontSize: "18px" }}
                  >
                    Session Logging Out
                  </h4>
                  <p
                    className="mb-3"
                    style={{ color: SECONDARYCOLOR, fontSize: "14px" }}
                  >
                    Your password has been changed successfully. For security
                    reasons, You need to login again with your new password.
                  </p>
                  <p
                    className="mb-3"
                    style={{
                      color: ACCENTCOLOR,
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Redirecting to login in {countdown} second
                    {countdown !== 1 ? "s" : ""}...
                  </p>
                  <div className="d-flex justify-content-center pt-2">
                    <Button
                      size={MEDIUM}
                      hoveredStyle={primaryButtonHoverStyle}
                      style={primaryButtonStyle}
                      id="reauth-modal-ok"
                      onClick={handleReauthModalConfirm}
                      type="button"
                    >
                      OK
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            </PortalOverlay>
          ) : null}
          {toggleSideBar && (
            <div
              className={Styles.sidebarWrapper}
              style={{ width: toggleCollapsedView ? "80px" : "250px" }}
            >
              <Sidebar
                collapsedView={toggleCollapsedView}
                toggleCollapseSideBar={() =>
                  setToggleCollapsedView(!toggleCollapsedView)
                }
                toggleSideBar={(value: boolean) => setToggleSidebar(value)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TopBar;
